import { useEffect } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { captureException } from '@sentry/react';

import { Button } from '@/components/ui/button';
import { isApiNotFoundError } from '@/lib/auth/apiError';
import { isMfaRequiredError } from '@/lib/auth/mfaError';
import Loader from '@/lib/components/Loader';

import Page404 from '../404';

/**
 * Route-level error boundary. Renders the appropriate screen per error kind:
 * MFA-gate denials show a redirect placeholder (navigation is handled by the
 * interceptor), router and API 404s (plain objects with a `status` attached
 * by the hey-api interceptor) render the 404 page, and everything else shows
 * the generic crash screen. Only unexpected errors are reported to Sentry —
 * MFA redirects and 404s are suppressed.
 */
const RootErrorBoundary = () => {
  const error = useRouteError() as Error;

  const isApiNotFound = isApiNotFoundError(error);

  useEffect(() => {
    // MFA-gate denials are an expected re-auth redirect, not a crash — don't
    // report them to Sentry. Same for API 404s (e.g. a soft-deleted or
    // missing policy): they render the 404 page, not the crash screen.
    if (!isMfaRequiredError(error) && !isApiNotFound) {
      captureException(error);
    }
  }, [error, isApiNotFound]);

  // Request-level MFA gate (403 + mfa_required): the global hey-api error
  // interceptor (handleMfaRequired) has ALREADY fired the toast + redirect to
  // /auth/login before this boundary rendered. We must NOT navigate again here
  // (a second navigation would diverge `from`, add a duplicate history entry,
  // and double-render under StrictMode). Just suppress the "Ooops!" crash UI
  // and show a brief, accessible placeholder while that navigation lands. The
  // role="status" + sr-only text announce the redirect to screen-reader users
  // (the Loader itself is a silent animated SVG).
  if (isMfaRequiredError(error)) {
    return (
      <div role="status">
        <span className="sr-only">Redirecting to login for verification…</span>
        <span aria-hidden="true">
          <Loader />
        </span>
      </div>
    );
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <Page404 />;
    }
  }

  if (isApiNotFound) {
    return <Page404 />;
  }

  return (
    <div className="bg-brand-dark-green pt-9">
      <div className="flex min-h-[10vh] pl-4">
        <Link to="/" key="Home" className="ml-[16px]">
          <img src="/assets/aspen_sm.svg" alt="Aspen logo" />
        </Link>
      </div>

      <div className="grid min-h-[90vh] items-center justify-center gap-2 bg-planets text-white">
        <img
          width={400}
          src="/assets/unexpected_error.svg"
          alt="An unexpected error has occurred"
        />

        <div className="grid items-center gap-4 self-start text-center">
          <h1 className="text-5xl">Ooops!</h1>

          <div className="grid gap-6">
            <p>An unexpected error has occurred</p>
            <div>
              <Button className="bg-white text-black">
                <Link to="/">Back to Dashboard</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RootErrorBoundary;
